import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Loader2 } from "lucide-react";

interface KeyRotationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (newKey: string, reason: string) => void;
  isLoading?: boolean;
  tenantName?: string;
}

export function KeyRotationDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  tenantName,
}: KeyRotationDialogProps) {
  const [newKey, setNewKey] = useState("");
  const [confirmKey, setConfirmKey] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = () => {
    setError(null);

    if (!newKey) {
      setError("New encryption key is required");
      return;
    }

    if (newKey.length < 32) {
      setError("Encryption key must be at least 32 characters");
      return;
    }

    if (newKey !== confirmKey) {
      setError("Keys do not match");
      return;
    }

    if (!reason.trim()) {
      setError("Reason for rotation is required");
      return;
    }

    onConfirm(newKey, reason);
  };

  const handleClose = () => {
    if (!isLoading) {
      setNewKey("");
      setConfirmKey("");
      setReason("");
      setError(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rotate Encryption Key</DialogTitle>
          <DialogDescription>
            {tenantName
              ? `Rotate the encryption key for ${tenantName}`
              : "Rotate the global encryption key"}
          </DialogDescription>
        </DialogHeader>

        <Alert variant="destructive" className="my-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Warning:</strong> Rotating the encryption key will require re-encrypting all
            existing encrypted data. This is an irreversible operation. Make sure you have a backup
            of your current key.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newKey">New Encryption Key</Label>
            <Input
              id="newKey"
              type="password"
              placeholder="Enter new encryption key (min 32 characters)"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmKey">Confirm New Key</Label>
            <Input
              id="confirmKey"
              type="password"
              placeholder="Re-enter the new encryption key"
              value={confirmKey}
              onChange={(e) => setConfirmKey(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Rotation</Label>
            <Textarea
              id="reason"
              placeholder="e.g., Scheduled rotation, Security incident, Compliance requirement..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isLoading}
              rows={3}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Rotate Key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
